import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Topbar from "../components/Topbar";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import {
  GraduationCap, BookOpen, ArrowRight, Trophy, ShoppingCart, Search, Clock, Loader2, CheckCircle2,
  Award, Download,
} from "lucide-react";
import { areaIcon, areaLabel } from "../lib/courseAreas";
import { eur } from "../lib/pricing";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Input } from "../components/ui/input";

const LEVEL_LABEL = { base: "Base", intermedio: "Intermedio", avanzato: "Avanzato" };

export default function StudenteDashboard() {
  const { user } = useAuth();
  const { canBuy } = useCart();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const validTabs = canBuy ? ["miei", "acquista", "attestati"] : ["miei", "attestati"];
  const requested = searchParams.get("tab");
  const tab = validTabs.includes(requested) ? requested : "miei";
  const setTab = (v) => setSearchParams(v === "miei" ? {} : { tab: v }, { replace: true });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/me/courses`);
        setCourses(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const ownedIds = courses.map((c) => c.course_id);

  return (
    <div data-testid="studente-dashboard" className="min-h-screen bg-brand-bg">
      <Topbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-brand-gold mb-2">
            <GraduationCap size={14} /> Area Studente
          </div>
          <h1 className="text-3xl sm:text-4xl text-brand-ink mb-2">
            Benvenuto, {user?.first_name}
          </h1>
          <p className="text-brand-muted max-w-2xl">
            {canBuy
              ? "Segui i tuoi corsi e acquistane di nuovi direttamente da qui."
              : "I corsi ai quali sei iscritto. Segui le lezioni nell'ordine indicato e completa i test per ottenere il certificato."}
          </p>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-6 flex flex-wrap gap-1 bg-white border border-brand-line rounded-sm p-1">
            <StTab value="miei" icon={GraduationCap} label="I miei corsi" />
            {canBuy && <StTab value="acquista" icon={ShoppingCart} label="Acquista corsi" />}
            <StTab value="attestati" icon={Award} label="I miei attestati" />
          </TabsList>
          <TabsContent value="miei">
            <MyCourses courses={courses} loading={loading} canBuy={canBuy} navigate={navigate} />
          </TabsContent>
          {canBuy && (
            <TabsContent value="acquista">
              <ShopCourses ownedIds={ownedIds} />
            </TabsContent>
          )}
          <TabsContent value="attestati">
            <MyCertificates navigate={navigate} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function StTab({ value, icon: Icon, label }) {
  return (
    <TabsTrigger value={value}
      className="rounded-sm data-[state=active]:bg-brand-blue data-[state=active]:text-white px-3 py-1.5 text-sm">
      <Icon size={15} className="mr-1.5" /> {label}
    </TabsTrigger>
  );
}

// ---------------------------------------------------------------------------
// I miei corsi
// ---------------------------------------------------------------------------
function MyCourses({ courses, loading, canBuy, navigate }) {
  if (loading) return <div className="text-center py-16 text-brand-muted">Caricamento corsi…</div>;
  if (courses.length === 0) {
    return (
      <div className="bg-white border border-dashed border-brand-line rounded-sm p-16 text-center">
        <BookOpen size={28} className="mx-auto text-brand-blue mb-3" />
        <div className="text-lg text-brand-ink mb-1">Nessun corso</div>
        <p className="text-sm text-brand-muted">
          {canBuy
            ? "Sfoglia e acquista un corso dalla scheda «Acquista corsi»."
            : "I corsi ti vengono assegnati dalla tua azienda."}
        </p>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {courses.map((c) => {
        const Icon = areaIcon(c.area);
        return (
          <div
            key={c.course_id}
            data-testid={`student-course-card-${c.course_id}`}
            className="bg-white border border-brand-line rounded-sm p-6 hover:border-brand-blue/40 hover:shadow-md transition-[box-shadow,border-color] duration-200 flex flex-col"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-sm bg-brand-blue/5 text-brand-blue">
                <Icon size={22} strokeWidth={1.75} />
              </div>
              {c.final_test_passed && (
                <span className="text-[10px] font-semibold uppercase tracking-widest bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-sm px-2 py-0.5 inline-flex items-center gap-1">
                  <Trophy size={10} /> Completato
                </span>
              )}
            </div>
            <h3 className="text-lg text-brand-blue mb-1 line-clamp-2">{c.title}</h3>
            <div className="text-xs text-brand-muted mb-3">
              {areaLabel(c.area)} · {c.modules_count} modul{c.modules_count === 1 ? "o" : "i"} · {c.lessons_total} lezion{c.lessons_total === 1 ? "e" : "i"}
            </div>
            <p className="text-sm text-brand-muted line-clamp-2 mb-4 flex-1">{c.description || ""}</p>
            <div className="mb-4">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-brand-muted">Progresso</span>
                <span className="font-semibold text-brand-blue" data-testid={`progress-pct-${c.course_id}`}>
                  {c.percent_completed}%
                </span>
              </div>
              <div className="h-1.5 bg-brand-line rounded-sm overflow-hidden">
                <div className="h-full bg-brand-blue transition-all" style={{ width: `${c.percent_completed}%` }} />
              </div>
              <div className="text-[10px] text-brand-muted mt-1">
                {c.lessons_completed}/{c.lessons_total} lezioni completate
              </div>
            </div>
            <button
              data-testid={`student-course-open-${c.course_id}`}
              onClick={() => navigate(`/studente/corsi/${c.course_id}`)}
              className="btn-primary inline-flex items-center justify-center gap-2 w-full"
            >
              {c.percent_completed > 0 ? "Continua" : "Inizia corso"} <ArrowRight size={14} />
            </button>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Acquista corsi (vetrina interna all'area riservata)
// ---------------------------------------------------------------------------
function ShopCourses({ ownedIds }) {
  const { addItem } = useCart();
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/public/catalog");
        setCatalog(data || []);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const q = search.trim().toLowerCase();
  const filtered = catalog.filter(
    (c) => !q || c.title.toLowerCase().includes(q) || areaLabel(c.area).toLowerCase().includes(q)
  );

  const add = (course) => {
    addItem({ id: course.id, title: course.title, price: course.price, area: course.area, level: course.level }, 1);
    toast.success(`“${course.title}” aggiunto al carrello.`);
  };

  return (
    <div className="bg-white border border-brand-line rounded-sm p-6">
      <p className="text-sm text-brand-muted mb-5">
        Scegli un corso e aggiungilo al carrello: completerai l'acquisto scegliendo il metodo di pagamento.
      </p>

      <div className="relative max-w-sm mb-6">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Cerca un corso o un'area…" className="pl-9 h-11 rounded-sm border-brand-line" />
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-brand-muted py-10 justify-center">
          <Loader2 className="animate-spin" size={18} /> Caricamento…
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-brand-muted py-8 text-center">Nessun corso trovato.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((c) => {
            const AreaIcon = areaIcon(c.area);
            const owned = ownedIds.includes(c.id);
            return (
              <div key={c.id} className="group bg-white border border-brand-line rounded-sm overflow-hidden hover:shadow-md hover:border-brand-blue/40 transition-[box-shadow,border-color] duration-200 flex flex-col">
                <div className="aspect-video bg-brand-blue/5 border-b border-brand-line flex items-center justify-center overflow-hidden">
                  <AreaIcon size={44} className="text-brand-blue/60" strokeWidth={1.25} />
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="text-[10px] uppercase tracking-widest bg-brand-blue/5 text-brand-blue border border-brand-blue/15 rounded-sm px-1.5 py-0.5">
                      {areaLabel(c.area)}
                    </span>
                    <span className="text-[10px] uppercase tracking-widest bg-brand-gold/10 text-brand-gold-deep border border-brand-gold/30 rounded-sm px-1.5 py-0.5">
                      {LEVEL_LABEL[c.level] || c.level}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-brand-blue mb-2 leading-snug">{c.title}</h3>
                  {c.short_description && (
                    <p className="text-sm text-brand-muted leading-relaxed mb-4 line-clamp-3">{c.short_description}</p>
                  )}
                  <div className="mt-auto pt-4 border-t border-brand-line flex items-end justify-between gap-3">
                    <div>
                      {c.duration_hours != null && (
                        <div className="flex items-center gap-1.5 text-xs text-brand-muted mb-1">
                          <Clock size={12} /> {c.duration_hours} ore
                        </div>
                      )}
                      <div className="text-2xl font-bold text-brand-blue">{eur(c.price)}</div>
                      <div className="text-[10px] uppercase tracking-widest text-brand-muted">IVA esclusa</div>
                    </div>
                    {owned ? (
                      <span className="inline-flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-2 py-1">
                        <CheckCircle2 size={13} /> Già iscritto
                      </span>
                    ) : (
                      <button onClick={() => add(c)}
                        className="btn-primary h-9 inline-flex items-center gap-1.5 px-3 text-sm">
                        <ShoppingCart size={15} /> Al carrello
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// I miei attestati
// ---------------------------------------------------------------------------
function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return iso; }
}

function MyCertificates({ navigate }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/me/certificates/summary");
      setRows(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const download = async (certId) => {
    const res = await api.get(`/me/certificates/${certId}/download`, { responseType: "blob" });
    const cd = res.headers["content-disposition"] || "";
    const m = cd.match(/filename="?([^"]+)"?/);
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = m ? m[1] : "Attestato.pdf";
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };
  const doDownload = async (id) => {
    try { await download(id); } catch (e) { toast.error(formatApiError(e)); }
  };
  const issueAndDownload = async (row) => {
    setBusy(row.course_id);
    try {
      const { data } = await api.post(`/me/courses/${row.course_id}/certificate`);
      toast.success("Attestato emesso.");
      await download(data.id);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="bg-white border border-brand-line rounded-sm p-6">
      <p className="text-sm text-brand-muted mb-5">
        Gli attestati dei corsi che hai completato superando il test finale. Scaricali in PDF (con QR di verifica).
      </p>
      {loading ? (
        <div className="flex items-center gap-2 text-brand-muted py-10 justify-center">
          <Loader2 className="animate-spin" size={18} /> Caricamento…
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-10">
          <Award size={30} className="mx-auto text-brand-blue/40 mb-3" />
          <p className="text-sm text-brand-muted">
            Nessun attestato. Completa un corso e supera il test finale per ottenerlo.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.course_id}
              className="flex flex-wrap items-center justify-between gap-3 border border-brand-line rounded-sm p-4">
              <div className="flex items-center gap-3 min-w-0">
                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-sm bg-brand-blue/5 text-brand-blue">
                  <Award size={18} />
                </span>
                <div className="min-w-0">
                  <div className="font-medium text-brand-ink truncate">{r.course_title}</div>
                  <div className="text-xs text-brand-muted">
                    {r.certificate_id ? `Emesso il ${fmtDate(r.issued_at)}`
                      : r.is_revoked ? "Attestato revocato"
                      : "Test finale superato"}
                  </div>
                </div>
              </div>
              <div className="shrink-0">
                {r.certificate_id ? (
                  <button onClick={() => doDownload(r.certificate_id)}
                    className="inline-flex items-center gap-1.5 rounded-sm border border-brand-blue text-brand-blue hover:bg-brand-blue/5 px-3 h-9 text-sm font-semibold">
                    <Download size={14} /> Scarica
                  </button>
                ) : r.can_issue ? (
                  <button onClick={() => issueAndDownload(r)} disabled={busy === r.course_id}
                    className="btn-primary h-9 inline-flex items-center gap-1.5 px-3 text-sm disabled:opacity-60">
                    {busy === r.course_id ? <Loader2 className="animate-spin" size={14} />
                      : <><Award size={14} /> Emetti e scarica</>}
                  </button>
                ) : r.needs_feedback ? (
                  <button onClick={() => navigate(`/studente/corsi/${r.course_id}`)}
                    className="inline-flex items-center gap-1.5 rounded-sm border border-brand-gold/40 text-brand-gold-deep bg-brand-gold/10 px-3 h-9 text-sm font-semibold">
                    Compila il questionario
                  </button>
                ) : r.missing_fields?.length ? (
                  <button onClick={() => navigate("/profilo")}
                    className="inline-flex items-center gap-1.5 rounded-sm border border-brand-gold/40 text-brand-gold-deep bg-brand-gold/10 px-3 h-9 text-sm font-semibold">
                    Completa il profilo
                  </button>
                ) : r.is_revoked ? (
                  <span className="text-xs text-brand-muted">Revocato dall'amministrazione</span>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
